/**
 * Shared Mapbox paint expressions for geofence polygon layers.
 *
 * Colours are status-driven (green for active, grey for inactive) and used by
 * both the devices map and the geofences map. Factories take the MUI theme
 * mode so callers can memoise on `theme.palette.mode`.
 */

const GEOFENCE_ACTIVE_COLOR = "#4CAF50";
const GEOFENCE_INACTIVE_COLOR_DARK = "#757575";
const GEOFENCE_INACTIVE_COLOR_LIGHT = "#9E9E9E";

const statusColorExpression = (mode) => [
  "case",
  ["==", ["get", "geofence_status"], "active"],
  GEOFENCE_ACTIVE_COLOR,
  mode === "dark"
    ? GEOFENCE_INACTIVE_COLOR_DARK
    : GEOFENCE_INACTIVE_COLOR_LIGHT,
];

/**
 * Fill paint for geofence polygons.
 * @param {string} mode - MUI palette mode ("light" | "dark")
 */
export const getGeofenceFillPaint = (mode) => ({
  "fill-color": statusColorExpression(mode),
  "fill-opacity": 0.2,
});

/**
 * Stroke (outline) paint for geofence polygons.
 * @param {string} mode - MUI palette mode ("light" | "dark")
 */
export const getGeofenceStrokePaint = (mode) => ({
  "line-color": statusColorExpression(mode),
  "line-width": 2,
  "line-opacity": 0.8,
});

/**
 * Circle paint for clustered geofence centre markers (stepped by count).
 * @param {string} mode - MUI palette mode ("light" | "dark")
 */
export const getGeofenceClusterPaint = (mode) => ({
  "circle-color": [
    "step",
    ["get", "point_count"],
    mode === "dark" ? "#2196F3" : "#1976D2", // small clusters (< 10)
    10,
    mode === "dark" ? "#FFC107" : "#F57C00", // medium clusters (10-30)
    30,
    mode === "dark" ? "#E91E63" : "#C2185B", // large clusters (> 30)
  ],
  "circle-radius": ["step", ["get", "point_count"], 20, 10, 30, 30, 40],
  "circle-stroke-width": 2,
  "circle-stroke-color": "#fff",
  "circle-opacity": 0.8,
});

/** Static symbol layout for cluster count labels. */
export const GEOFENCE_CLUSTER_COUNT_LAYOUT = {
  "text-field": "{point_count_abbreviated}",
  "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
  "text-size": 14,
  "text-allow-overlap": true,
};

/**
 * Circle paint for unclustered geofence centre markers (status-coloured).
 * @param {string} mode - MUI palette mode ("light" | "dark")
 */
export const getGeofenceCenterPaint = (mode) => ({
  "circle-color": [
    "case",
    ["==", ["get", "geofence_status"], "active"],
    mode === "dark" ? "#66BB6A" : "#4CAF50", // active
    mode === "dark" ? "#9E9E9E" : "#757575", // inactive
  ],
  "circle-radius": 10,
  "circle-stroke-width": 2,
  "circle-stroke-color": "#fff",
  "circle-opacity": 0.9,
});

/** Static symbol layout for geofence centre name labels. */
export const GEOFENCE_CENTER_LABEL_LAYOUT = {
  "text-field": ["get", "geofence_name"],
  "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
  "text-size": 12,
  "text-anchor": "top",
  "text-offset": [0, 1.5],
  "text-max-width": 12,
  "text-allow-overlap": false,
  "text-optional": true,
};

/**
 * Text paint for geofence centre name labels.
 * @param {string} mode - MUI palette mode ("light" | "dark")
 */
export const getGeofenceCenterLabelPaint = (mode) => ({
  "text-color": mode === "dark" ? "#ffffff" : "#000000",
  "text-halo-color": mode === "dark" ? "#1a1a1a" : "#ffffff",
  "text-halo-width": 2,
  "text-halo-blur": 1,
});
